if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * - Main PDP item is added with its properties as usual.
     * - Side logo products are added per selected side with qty = mainQty × multiplier.
     * - Shared add-ons (Reflective Vinyl, Apply Logo, Number of Colors) are added with qty = selectedSides × mainQty × multiplier.
     * @param {SubmitEvent} evt
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;
      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      formData.append('sections_url', window.location.pathname);

      try {
        // 1) Add the MAIN product (normal form post)
        const mainResponse = await fetch(theme.routes.cartAdd, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData
        });

        const mainData = await mainResponse.json();
        if (!mainResponse.ok) throw new Error(mainData.message || "Main product error");

        // 2) Build add-ons
        const addons = [];
        const mainQty = parseInt(formData.get("quantity")) || 1;
        const mainVariantId = Number(formData.get("id"));

        // ----- BEGIN: Multipliers keyed by MAIN product variant ID -----
        // For these main variant IDs, add-ons will use: (main qty) × (multiplier)
        const ADDON_MULT_BY_MAIN_VARIANT = {
          50028456476919: 100, 
          50028456509687: 200, 50044048212215: 200,
          50028456542455: 360, 50044048244983: 360, 50044059877623: 360,
          50028456575223: 720, 50044048277751: 720, 50044059910391: 720, 50044060532983: 720,
          50627788177655: 10, 50627788210423: 25, 50627788243191: 50, 50627788275959: 100
        };
        // Determine the multiplier for the selected main variant; default = 1
        const addonMultiplier = ADDON_MULT_BY_MAIN_VARIANT[mainVariantId] || 1;
        const computeAddonQty = (baseQty) => baseQty * addonMultiplier;
        // ----- END: Multipliers keyed by MAIN product variant ID -----

        // Count selected sides (Front/Back/Left/Right)
        const checkedSides = Array.from(this.form.querySelectorAll(".logo-position-checkbox:checked"));
        const selectedSidesCount = checkedSides.length;

        // (A) Side-specific logo products: add one entry per selected side, qty = mainQty × multiplier
        checkedSides.forEach(cb => {
          const sideVar = Number(cb.dataset.variant);
          const sidePos = cb.dataset.position || '';
          if (sideVar) {
            addons.push({
              id: sideVar,
              quantity: computeAddonQty(mainQty),
              properties: { "Logo Position": sidePos }
            });
          }
        });

        // (B) Shared add-ons (NEW logic): only if at least one side is selected
        if (selectedSidesCount > 0) {
          // Apply Logo (shared)
          const applyLogoRadio = this.form.querySelector('input[name="properties[Apply Logo]"]:checked');
          if (applyLogoRadio && applyLogoRadio.dataset && applyLogoRadio.dataset.variant) {
            const applyVar = Number(applyLogoRadio.dataset.variant);
            if (applyVar) {
              addons.push({
                id: applyVar,
                quantity: computeAddonQty(mainQty * selectedSidesCount)
              });
            }
          }

          // Vinyl Color (shared): only if "Reflective" selected (has data-variant)
          const vinylRadio = this.form.querySelector('input[name="properties[Logo Vinyl Color]"]:checked');
          if (vinylRadio && vinylRadio.dataset && vinylRadio.dataset.variant) {
            const vinylVar = Number(vinylRadio.dataset.variant);
            if (vinylVar) {
              addons.push({
                id: vinylVar,
                quantity: computeAddonQty(mainQty * selectedSidesCount)
              });
            }
          }

          // NEW: Number of Colors in Logo (shared)
          // 1 color = no charge, else add the mapped variant with qty = sides × mainQty × multiplier
          const COLOR_VARIANT_FALLBACK = {
            '2': 50144222052599,
            '3': 50144222085367,
            '4': 50144222118135
          };
          const colorsChoice = this.form.querySelector('input[name="properties[Logo Colors]"]:checked');
          if (colorsChoice && colorsChoice.value !== '1') {
            const colorVariantId =
              (colorsChoice.dataset && colorsChoice.dataset.variant)
                ? Number(colorsChoice.dataset.variant)
                : (COLOR_VARIANT_FALLBACK[colorsChoice.value] || null);

            if (colorVariantId) {
              addons.push({
                id: colorVariantId,
                quantity: computeAddonQty(mainQty * selectedSidesCount)
              });
            }
          }
        }

        // (C) Optional: Stencil upsell (kept as-is; qty scales by mainQty × multiplier)
        (() => {
          const selectEl = this.form.querySelector('#stencil-option');
          if (!selectEl || selectEl.value === 'none') return;

          const stencilVarId = (this.form.querySelector('#stencil-variant-id')?.value || '').trim();
          if (!stencilVarId) return;

          const stencilText = (this.form.querySelector('#stencil-text')?.value || '').trim();
          const stencilOrientation = this.form.querySelector('input[name="properties[Stencil Orientation]"]:checked')?.value || '';
          const stencilHandle = (this.form.querySelector('#stencil-product-handle')?.value || '').trim();
          const stencilLabel = selectEl.options[selectEl.selectedIndex]?.text?.trim() || 'Stencil';

          const properties = {
            "Attached To": mainVariantId ? `Variant ${mainVariantId}` : '',
            "Stencil Option": stencilLabel,
            "Stencil Text": stencilText,
            "Stencil Orientation": stencilOrientation
          };
          if (stencilHandle) properties["Stencil Product Handle"] = stencilHandle;

          addons.push({
            id: Number(stencilVarId),
            quantity: computeAddonQty(mainQty)
            // ,properties // uncomment to include stencil properties on the line
          });
        })();

        // 3) Add all add-ons in one request
        if (addons.length) {
          const addonResponse = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ items: addons })
          });
          const addonData = await addonResponse.json();
          if (!addonResponse.ok) throw new Error(addonData.message || "Addon product error");
        }

        // 4) Post-ATC behavior
        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => { window.location.href = theme.routes.cart; }, 300);
        } else {
          ProductForm.updateCartIcon(mainData);
          const quickAddDrawer = this.closest("quick-add-drawer");
          if (quickAddDrawer) quickAddDrawer.addedToCart();
          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(mainData, !quickAddDrawer && theme.settings.afterAtc === "drawer");
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector("cart-items");
              if (cartItems) cartItems.refresh();
            }
          }, 700);
        }

      } catch (err) {
        console.error("ATC error", err);
        this.dispatchEvent(new CustomEvent("on:cart:error", {
          bubbles: true,
          detail: { error: this.errorMsg?.textContent }
        }));
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.submitBtn.classList.add("is-success");
        this.submitBtn.removeAttribute("aria-disabled");
        setTimeout(() => {
          this.submitBtn.classList.remove("is-loading", "is-success");
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string|Array<string>} [error=false]
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
